import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    Font,
    Link,
} from '@react-pdf/renderer';
import moment from 'moment';
import { FundRequest } from '~/types/fundrequests';
import { ExpensesTable } from './ExpensesTable';

type HistoryItem = {
    user_id: number;
    person_id: number;
    person_name: string;
    status: string;
    updated_date: string;
    description?: string;
};

// Register a font to ensure text renders
Font.register({
    family: 'Helvetica',
    fonts: [
        {
            src: 'https://fonts.gstatic.com/s/helveticaneue/v70/1Ptsg8zYS_SKggPNyCg4TYFv.ttf',
        },
        {
            src: 'https://fonts.gstatic.com/s/helveticaneue/v70/1Ptsg8zYS_SKggPNyCg4QYFv.ttf',
            fontWeight: 'bold',
        },
    ],
});

const styles = StyleSheet.create({
    page: {
        padding: 30,
        fontFamily: 'Helvetica',
    },
    section: {
        marginBottom: 20,
    },
    title: {
        fontSize: 24,
        marginBottom: 10,
        fontFamily: 'Helvetica',
    },
    subtitle: {
        fontSize: 18,
        marginBottom: 10,
        fontFamily: 'Helvetica',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 20,
    },
    text: {
        fontSize: 12,
        marginBottom: 5,
        fontFamily: 'Helvetica',
    },
    label: {
        fontWeight: 'bold',
        fontFamily: 'Helvetica',
    },
    note: {
        marginBottom: 10,
        padding: 10,
        borderWidth: 1,
        borderColor: '#E5E7EB',
        borderRadius: 4,
    },
    noteText: {
        fontStyle: 'italic',
        marginBottom: 5,
        fontSize: 9,
    },
    noteMeta: {
        fontSize: 8,
        color: '#6B7280',
    },
    columns: {
        flexDirection: 'row',
        marginBottom: 20,
    },
    column: {
        flex: 1,
        paddingRight: 15,
    },
    historySection: {
        marginBottom: 20,
        borderWidth: 1,
        borderColor: '#E5E7EB',
        borderRadius: 4,
        padding: 10,
    },
    historyItem: {
        marginBottom: 8,
        paddingBottom: 8,
        borderBottomWidth: 1,
        borderBottomColor: '#E5E7EB',
    },
    historyItemLast: {
        marginBottom: 0,
        paddingBottom: 0,
        borderBottomWidth: 0,
        borderBottomColor: '#E5E7EB',
    },
    historyText: {
        fontSize: 10,
        marginBottom: 3,
    },
    historyMeta: {
        fontSize: 8,
        color: '#6B7280',
    },
});

interface FundRequestDocumentProps {
    fundRequest: FundRequest;
    requestHistory: HistoryItem[];
}

export const FundRequestDocument = ({
    fundRequest,
    requestHistory,
}: FundRequestDocumentProps) => {
    const lastUpdated = moment(fundRequest.updated_date).format('MM/DD/YY');
    const {
        name,
        email,
        phone,
        address_1,
        address_2,
        city,
        state,
        zip_code,
        payment_type,
    } = fundRequest.vendor;

    const { patient } = fundRequest.fund_info;

    const renderHistoryItem = (
        item: HistoryItem,
        isLast: boolean
    ): JSX.Element => {
        // Safety check for null/undefined item or missing properties
        if (!item || typeof item !== 'object') {
            return <View key={Math.random()}></View>;
        }

        const itemStyles = [styles.historyItem];
        if (isLast) {
            itemStyles.push(styles.historyItemLast);
        }

        return (
            <View key={item.user_id || Math.random()} style={itemStyles}>
                <Text style={styles.historyText}>
                    Status changed to: {item.status || 'Unknown'}
                </Text>
                <Text style={styles.historyMeta}>
                    By {item.person_name || 'Unknown'} on{' '}
                    {item.updated_date
                        ? moment(item.updated_date).format('MM/DD/YYYY h:mm:ss A')
                        : 'Unknown date'}
                </Text>
                {item.description && (
                    <Text style={[styles.historyMeta, { marginTop: 4 }]}>
                        {item.description}
                    </Text>
                )}
            </View>
        );
    };

    return (
        <Document>
            <Page size='A4' style={styles.page}>
                <View style={styles.section}>
                    <Text style={styles.title}>
                        Fund Request for {patient.cf_patient_ntaf_id}
                    </Text>
                    <View style={styles.header}>
                        <Text style={styles.text}>
                            Client: {patient.person.first_name}{' '}
                            {patient.person.last_name}
                        </Text>
                        <Text style={styles.text}>
                            Campaign Type: {patient.campaign_type}
                        </Text>
                        <Text style={styles.text}>
                            Campaign ID: {fundRequest.campaign}
                        </Text>
                    </View>
                    <View style={styles.header}>
                        <Text style={styles.text}>
                            Status:{' '}
                            {fundRequest.fund_request_status?.label || 'N/A'}
                        </Text>
                        <Text style={styles.text}>
                            Request ID: {fundRequest.id}
                        </Text>
                        <Text style={styles.text}>
                            Last Updated: {lastUpdated}
                        </Text>
                    </View>
                </View>

                <View style={styles.columns}>
                    <View style={styles.column}>
                        <View style={styles.section}>
                            <Text style={styles.subtitle}>Payee Details</Text>
                            <View>
                                <View style={styles.text}>
                                    <Text style={styles.label}>Name: </Text>
                                    <Text>{name}</Text>
                                </View>
                                <View style={styles.text}>
                                    <Text style={styles.label}>Address: </Text>
                                    <Text>
                                        {address_1}
                                        {address_2 ? `, ${address_2}` : ''}
                                        {city ? `, ${city}` : ''}
                                        {state?.abbreviation
                                            ? `, ${state.abbreviation}`
                                            : ''}
                                        {zip_code ? ` ${zip_code}` : ''}
                                    </Text>
                                </View>
                                <View style={styles.text}>
                                    <Text style={styles.label}>Phone: </Text>
                                    <Text>{phone}</Text>
                                </View>
                                <View style={styles.text}>
                                    <Text style={styles.label}>Email: </Text>
                                    <Text>{email}</Text>
                                </View>
                                <View style={styles.text}>
                                    <Text style={styles.label}>
                                        Preferred Payment Method:{' '}
                                    </Text>
                                    <Text>{payment_type}</Text>
                                </View>
                            </View>
                        </View>

                        <View style={styles.section}>
                            <Text style={styles.subtitle}>
                                Financial Coordinator
                            </Text>
                            <View>
                                <View style={styles.text}>
                                    <Text style={styles.label}>Name: </Text>
                                    <Text>
                                        {fundRequest.financial_coordinator
                                            ? `${fundRequest.financial_coordinator.first_name} ${fundRequest.financial_coordinator.last_name}`
                                            : 'Not Assigned'}
                                    </Text>
                                </View>
                            </View>
                        </View>
                    </View>

                    <View style={styles.column}>
                        <View style={styles.section}>
                            <Text style={styles.subtitle}>Notes</Text>
                            {fundRequest.notes && fundRequest.notes.length > 0 ? (
                                fundRequest.notes.map((note) => (
                                    <View key={note.id} style={styles.note}>
                                        <Text style={styles.noteText}>
                                            {note.note}
                                        </Text>
                                        <Text style={styles.noteMeta}>
                                            Added by{' '}
                                            {note.created_by.first_name}{' '}
                                            {note.created_by.last_name} on{' '}
                                            {moment(note.created_date).format(
                                                'MM/DD/YYYY'
                                            )}
                                        </Text>
                                    </View>
                                ))
                            ) : (
                                <Text style={styles.text}>
                                    No notes available
                                </Text>
                            )}
                        </View>
                    </View>
                </View>

                <ExpensesTable fundRequest={fundRequest} />

                <View style={styles.historySection}>
                    <Text style={styles.subtitle}>Request History</Text>
                    {requestHistory.length > 0 ? (
                        requestHistory.map((item, index) =>
                            renderHistoryItem(
                                item,
                                index === requestHistory.length - 1
                            )
                        )
                    ) : (
                        <Text style={styles.text}>No history available</Text>
                    )}
                </View>
            </Page>
        </Document>
    );
};

